import { alpha, type Theme } from '@mui/material'
import type { Appearance } from '@stripe/stripe-js'

import {
  fieldBorderColor,
  fieldFill,
  fieldAccentColor,
  fieldErrorColor,
  FIELD_RADIUS,
  fonts,
} from '@repo/ui'

/**
 * VERA-1095 — the Stripe `appearance` object that makes `PaymentElement` render
 * as one of OUR fields.
 *
 * WHY THIS FILE EXISTS AT ALL. Embedded Checkout could not be styled: its only
 * lever is the session's `branding_settings`, which (measured on 2026-08-25
 * against the live API, with a `background_color: #ff0000` probe session)
 * colours the order-summary pane and nothing on the payment-form pane. Elements
 * is a different integration with a real appearance API, and this is where that
 * API is pointed at the same tokens every other field on the signup form reads.
 *
 * IT DERIVES, IT DOES NOT DECLARE. Every value below comes from `@repo/ui`'s
 * `fieldChrome` — the §31 single source that the platform `TextField` -> `Input`
 * chain the FIRST THREE STEPS of this form are built from also reads. That is
 * the whole point: a hardcoded hex here would agree with those fields on the day
 * it was written and silently drift the first time the token moved, which is
 * exactly the failure §31 was written about. If the field border changes, this
 * changes with it.
 *
 * Concretely, the fields on steps 1-3 are `components/input/fields/TextField`,
 * which renders `@repo/ui`'s `Input`: `border-radius: 8`, `1px solid
 * fieldBorderColor` (`primary[100]`), `background: fieldFill` (#fafafa light /
 * #1f1f23 dark), `font-size: 16`, `padding: 12`. Those five numbers are what the
 * rules below reproduce. Note this is deliberately NOT the SignInPage treatment
 * (fill-only in dark, `alpha(text.primary, 0.23)` border in light) — that is a
 * different page's override, and this step must match the three steps the
 * visitor just filled in, not a page they were never on.
 *
 * FONTS ARE NOT PART OF THIS OBJECT. `variables.fontFamily` only names a family;
 * the iframe cannot load it. `Elements`' separate `fonts: [{cssSrc}]` option is
 * what actually delivers Lato into the frame, and without it Stripe silently
 * falls back to its own sans — which is, on its own, most of why an embedded
 * form reads as somebody else's. See SignupPaymentStep's `ELEMENTS_FONTS`.
 */
/**
 * VRG-315: there are TWO `@mui/material` copies in the pnpm store (one resolved
 * under react@18, one under react@19), so the `Theme` this file imports and the
 * `Theme` the `@repo/ui` helpers were compiled against are structurally
 * identical and nominally different — `tsc` reports every call as a type
 * mismatch, and on a shape this large it gives up with "excessive complexity"
 * rather than a useful message.
 *
 * Derived from the helper's own signature rather than written as `any`, so the
 * cast asserts only the thing that is actually true (same shape, different
 * declaration site) and stops compiling the day the helper's parameter really
 * changes. Delete the alias, and the `chrome()` calls with it, when the
 * duplicate is resolved.
 */
type FieldChromeTheme = Parameters<typeof fieldFill>[0]

export const signupPaymentAppearance = (theme: Theme): Appearance => {
  const chrome = theme as unknown as FieldChromeTheme
  const isDark = theme.palette.mode === 'dark'
  const border = fieldBorderColor(chrome)
  const accent = fieldAccentColor(chrome)
  const danger = fieldErrorColor(chrome)
  // The soft brand ring §31 puts on a focused field. Read from the same token
  // the CSS helper does (`brand['600-6%']`), spelled out here because Stripe
  // wants a complete `box-shadow` string rather than a colour.
  const focusRing = `0 0 0 3px ${
    (theme.palette as unknown as { brand?: Record<string, string> }).brand?.['600-6%'] ??
    alpha(accent, 0.06)
  }`

  return {
    // The base only sets sane defaults for the parts `rules` does not reach
    // (icons, the saved-method radio, the Link chrome). Everything visible is
    // overridden below — picking the right base alone gets nowhere near.
    theme: isDark ? 'night' : 'stripe',
    variables: {
      colorPrimary: accent,
      // The BACKGROUND OF A FIELD, not of the container. Stripe uses this token
      // for input surfaces, which is why it is `fieldFill` and not
      // `background.default`. The container itself is transparent — see the
      // step component; a second panel behind the form is the "weird
      // background" this rebuild exists to remove.
      colorBackground: fieldFill(chrome),
      colorText: theme.palette.text.primary,
      colorTextSecondary: theme.palette.text.secondary,
      colorTextPlaceholder: theme.palette.text.secondary,
      colorDanger: danger,
      colorIcon: theme.palette.text.secondary,
      fontFamily: fonts.primary,
      // 16px, matching `Input`'s own `fontSize: 16`. Also the value that stops
      // iOS Safari zooming the page when a field takes focus.
      fontSizeBase: '16px',
      borderRadius: `${FIELD_RADIUS}px`,
      // Stripe's own default is 4px; ours is the 4px theme base too
      // (working-conventions §29), so spacing derived from it lands on the same
      // grid as the rest of the form.
      spacingUnit: '4px',
    },
    rules: {
      '.Input': {
        // 14px vertical / 12px horizontal, and the asymmetry with our own
        // field (12px all round) is deliberate — see the height note below.
        // The horizontal 12px is the one that has to match exactly, because it
        // sets where the text starts relative to the border, which sits
        // directly under the email field two steps back.
        padding: '14px 12px',
        // THE CONTROL HEIGHT: 49px, matching a platform field exactly. It does
        // not come for free, and the numbers here are MEASURED rather than
        // derived, because Stripe's box does not decompose the way ours does.
        //
        // Ours: MUI's input content box is `line-height: 1.4375em` — 23px at
        // our 16px base — so 12 + 23 + 12 + 2 = 49px. Verified in the browser
        // on this very form's step 1.
        //
        // Stripe's, with only padding and font-size matched, rendered 42px:
        // every colour, radius and typeface agreed and the row still read as a
        // different control, 7px shorter than the email field two steps back.
        // That is exactly the near-miss that survives a glance. Setting
        // line-height to our 23px took it to 45; the remaining 4px is the
        // vertical padding above (14 rather than 12). Both were arrived at by
        // measuring the rendered field, not by reading Stripe's stylesheet —
        // the element is inside a cross-origin iframe, so its computed style
        // cannot be read back and the outer frame's height is the only signal.
        //
        // Re-measure if you touch either number, and re-measure against step 1
        // rather than against this comment.
        lineHeight: '23px',
        backgroundColor: fieldFill(chrome),
        border: `1px solid ${border}`,
        boxShadow: 'none',
        color: theme.palette.text.primary,
        transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
      },
      // §31: hover and focus are the SAME brand border; focus adds the ring.
      '.Input:hover': {
        border: `1px solid ${accent}`,
      },
      '.Input:focus': {
        border: `1px solid ${accent}`,
        boxShadow: focusRing,
      },
      '.Input--invalid': {
        border: `1px solid ${danger}`,
        boxShadow: 'none',
        color: theme.palette.text.primary,
      },
      '.Input--invalid:focus': {
        border: `1px solid ${danger}`,
        // Same geometry as the brand ring, error colour — `fieldErrorRing`'s
        // rule, restated because Stripe needs the literal shadow string.
        boxShadow: `0 0 0 3px ${alpha(danger, 0.06)}`,
      },
      '.Input::placeholder': {
        color: theme.palette.text.secondary,
        // `Input` sets 0.65 on its own placeholder; same value so a card field
        // and an email field are equally quiet before they are filled.
        opacity: '0.65',
      },
      // `FieldLabel`: body3 (14/18) and an 8px gap down to the control.
      '.Label': {
        fontSize: '14px',
        lineHeight: '18px',
        fontWeight: '400',
        color: theme.palette.text.primary,
        marginBottom: '8px',
      },
      '.Error': {
        fontSize: '12px',
        lineHeight: '18px',
        color: danger,
        marginTop: '4px',
      },
      // The payment-method tabs (card / wallet). Only ever one tab today
      // (card-only, set server-side), but styled so that enabling a second one
      // later does not surface an unstyled control.
      '.Tab': {
        backgroundColor: fieldFill(chrome),
        border: `1px solid ${border}`,
        boxShadow: 'none',
        color: theme.palette.text.secondary,
      },
      '.Tab:hover': {
        border: `1px solid ${accent}`,
        color: theme.palette.text.primary,
      },
      '.Tab--selected': {
        border: `1px solid ${accent}`,
        boxShadow: focusRing,
        color: theme.palette.text.primary,
      },
      '.TabIcon--selected': {
        fill: accent,
      },
      '.CheckboxInput': {
        backgroundColor: fieldFill(chrome),
        border: `1px solid ${border}`,
        boxShadow: 'none',
      },
      '.CheckboxInput--checked': {
        backgroundColor: accent,
        border: `1px solid ${accent}`,
      },
    },
  }
}
